import { Box, Button, Card, Divider, Stack, Tooltip, Typography } from '@mui/material';
import { Trial_Status, TrialsQuery } from '@generated/graphql';
import { AssessmentStatus, useToast, webApp } from '@myhrbox/myhrbox-frontend-core';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ASSESSMENT_STATUS_TITLE } from 'constants/assessment';

type AssessmentCardProps = NonNullable<TrialsQuery['trials']['data']>[number];

const AssessmentCard = ({ assessment }: { assessment: AssessmentCardProps }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showErrorToast } = useToast();
  const isCompleted = assessment.status === Trial_Status.Completed;
  const GREEN_CARD_STATUSES = [Trial_Status.Completed];
  const BLUE_CARD_STATUSES = [Trial_Status.Canceled, Trial_Status.Started];

  const getCardColor = () => {
    if (GREEN_CARD_STATUSES.includes(assessment.status)) {
      return 'rgba(68, 191, 120, 0.24)';
    }
    if (BLUE_CARD_STATUSES.includes(assessment.status)) {
      return 'background.default';
    }
    return undefined;
  };

  const backgroundColor = getCardColor();

  const CLICKABLE_CARDS = [
    Trial_Status.Received,
    Trial_Status.Created,
    Trial_Status.Started,
    Trial_Status.Suspended,
  ];
  const isClickableCard = CLICKABLE_CARDS.includes(assessment.status);

  const navigateToAssessment = () => navigate(assessment.id);

  const reportFile = assessment.candidateReportFiles?.find((f) => f.file.url)?.file.url;
  const isTelegram = webApp?.platform !== 'unknown';

  const downloadReport = () => {
    if (reportFile) {
      if (isTelegram) {
        webApp?.openLink(reportFile);
      } else {
        window.open(reportFile);
      }
    } else {
      showErrorToast(t('assessmentList.reportNotCompleted'));
    }
  };

  const getReportButtonTooltipTitle = () => {
    if (!assessment.examination.isSendResult) return t('assessmentList.reportNotAllowed');
    if (!assessment.isAssessmentComplete) return t('assessmentList.reportNotCompleted');

    return undefined;
  };

  const isReportButtonDisabled =
    !assessment.examination.isSendResult || !assessment.isAssessmentComplete;

  return (
    <Card
      onClick={isClickableCard ? navigateToAssessment : undefined}
      sx={{
        cursor: isClickableCard ? 'pointer' : 'default',
        backgroundColor,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}>
      <Typography variant='h20' mb={1.2}>
        {assessment.examination.competencyMap.title}
      </Typography>
      <Stack sx={{ marginTop: 'auto' }}>
        <Divider sx={{ borderColor: 'base.200' }} />
        <Typography variant='subtitle18rg' sx={{ color: 'base.500', my: 2 }}>
          {assessment.companyCandidate.company.shortName}
        </Typography>
        <Stack direction='row' justifyContent='space-between' sx={{ marginTop: 'auto' }}>
          <AssessmentStatus
            status={assessment.status}
            title={t(ASSESSMENT_STATUS_TITLE[assessment.status])}
          />
          {isCompleted && (
            <Tooltip title={getReportButtonTooltipTitle()}>
              <Box>
                <Button
                  variant='contained'
                  onClick={downloadReport}
                  disabled={isReportButtonDisabled}
                  sx={{
                    backgroundColor: 'status.started',
                    '&:hover': { backgroundColor: 'green.contrast' },
                  }}>
                  {t('assessmentList.downloadReport')}
                </Button>
              </Box>
            </Tooltip>
          )}
        </Stack>
      </Stack>
    </Card>
  );
};

export default AssessmentCard;
